/**
 * DepartmentEmailModule - Enhanced email component for departments
 * Features: Inbox, Sent folder, Read/Unread status, Full email view
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import EmailViewModal from './email/EmailViewModal';
import {
  Mail,
  Send,
  Inbox,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  User,
  Paperclip,
  X,
  ChevronDown,
  Loader2,
  Settings,
  Bell,
  BellOff,
  Save,
  MessageSquare,
  Megaphone,
  Calendar,
  Circle,
  CheckCheck,
  Trash2,
  Reply,
  Forward,
  Star,
  StarOff,
  Archive,
  MailOpen,
  SendHorizontal
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

// Department email configuration
const DEPARTMENT_EMAILS = {
  finance: { email: 'finance@cccvob.com', name: 'Finance Department', color: 'emerald' },
  choir: { email: 'choir@cccvob.com', name: 'Choir Ministry', color: 'purple' },
  usher: { email: 'welfare@cccvob.com', name: 'Usher Department (Welfare)', color: 'orange' },
  secretary: { email: 'info@cccvob.com', name: 'Secretarial & Admin', color: 'slate' },
  secretarial: { email: 'info@cccvob.com', name: 'Secretarial & Admin', color: 'slate' },
  media: { email: 'media@cccvob.com', name: 'Media Department', color: 'red' },
  sundayschool: { email: 'sundayschool@cccvob.com', name: 'Sunday School', color: 'green' },
  shepherd: { email: 'shepherd@cccvob.com', name: "Shepherd's Corner", color: 'indigo' },
  communications: { email: 'info@cccvob.com', name: 'Communications', color: 'blue' }
};

// Notification type options
const NOTIFICATION_TYPES = [
  { value: 'realtime', label: 'Real-time', description: 'Get notified immediately', icon: Bell },
  { value: 'daily_digest', label: 'Daily Digest', description: 'Summary at 9:00 AM', icon: Calendar },
  { value: 'weekly_digest', label: 'Weekly Digest', description: 'Summary every Monday', icon: Calendar },
  { value: 'none', label: 'No Notifications', description: 'Turn off all alerts', icon: BellOff }
];

const DepartmentEmailModule = ({ department, adminUser, compact = false }) => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [deptMessages, setDeptMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [readEmails, setReadEmails] = useState(new Set());
  const [starredEmails, setStarredEmails] = useState(new Set());
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    notification_type: 'realtime',
    notify_on_new_message: true,
    notify_on_shepherd_broadcast: true,
    notify_on_announcements: false,
    digest_time: '09:00',
    digest_day: 'monday'
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  const [composeData, setComposeData] = useState({
    to: '',
    subject: '',
    body: '',
    toDepartment: '',  // For inter-department messaging
    isToDepartment: false  // Toggle for department vs external email
  });

  const deptConfig = DEPARTMENT_EMAILS[department] || {
    email: 'info@cccvob.com',
    name: department,
    color: 'blue'
  };

  // Load read/starred status from localStorage
  useEffect(() => {
    const storedRead = localStorage.getItem(`email_read_${department}`);
    const storedStarred = localStorage.getItem(`email_starred_${department}`);
    if (storedRead) setReadEmails(new Set(JSON.parse(storedRead)));
    if (storedStarred) setStarredEmails(new Set(JSON.parse(storedStarred)));
  }, [department]);

  // Save read/starred status to localStorage
  const saveReadStatus = (newReadSet) => {
    localStorage.setItem(`email_read_${department}`, JSON.stringify([...newReadSet]));
    setReadEmails(newReadSet);
  };

  const saveStarredStatus = (newStarredSet) => {
    localStorage.setItem(`email_starred_${department}`, JSON.stringify([...newStarredSet]));
    setStarredEmails(newStarredSet);
  };

  // Mark email as read
  const markAsRead = (emailId) => {
    const newReadSet = new Set(readEmails);
    newReadSet.add(emailId);
    saveReadStatus(newReadSet);
  };

  // Toggle star
  const toggleStar = (emailId, e) => {
    e.stopPropagation();
    const newStarredSet = new Set(starredEmails);
    if (newStarredSet.has(emailId)) {
      newStarredSet.delete(emailId);
    } else {
      newStarredSet.add(emailId);
    }
    saveStarredStatus(newStarredSet);
  };

  // Check email connection status
  const checkConnection = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email/status/${department}`);
      setConnectionStatus(res.data);
    } catch (error) {
      setConnectionStatus({ connected: false, error: 'Connection check failed' });
    }
  }, [department]);

  // Load inbox emails
  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email/inbox/${department}?limit=50`);
      setEmails(res.data.emails || []);
    } catch (error) {
      console.error('Error loading emails:', error);
      setEmails([]);
    }
    setLoading(false);
  }, [department]);

  // Load sent emails
  const loadSentEmails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email/sent/${department}?limit=50`);
      setSentEmails(res.data.emails || []);
    } catch (error) {
      console.error('Error loading sent emails:', error);
      // If API doesn't exist, use localStorage fallback
      const stored = localStorage.getItem(`sent_emails_${department}`);
      if (stored) setSentEmails(JSON.parse(stored));
    }
    setLoading(false);
  }, [department]);

  // Load notification settings
  const loadNotificationSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email/notifications/${department}`);
      setNotificationSettings({
        notification_type: res.data.notification_type || 'realtime',
        notify_on_new_message: res.data.notify_on_new_message ?? true,
        notify_on_shepherd_broadcast: res.data.notify_on_shepherd_broadcast ?? true,
        notify_on_announcements: res.data.notify_on_announcements ?? false,
        digest_time: res.data.digest_time || '09:00',
        digest_day: res.data.digest_day || 'monday'
      });
      setSettingsLoaded(true);
    } catch (error) {
      console.error('Error loading notification settings:', error);
      setSettingsLoaded(true);
    }
  }, [department]);

  // Load inter-department messages
  const loadDepartmentMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/department-messages?department=${department}&direction=all&sender_id=${adminUser?.id || ''}`);
      setDeptMessages(res.data.messages || []);
    } catch (error) {
      console.error('Error loading department messages:', error);
      setDeptMessages([]);
    }
  }, [department, adminUser?.id]);

  // Save notification settings
  const saveNotificationSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/email/notifications/${department}`, {
        department,
        ...notificationSettings
      });
      alert('Notification settings saved successfully!');
    } catch (error) {
      console.error('Error saving notification settings:', error);
      alert(error.response?.data?.detail || 'Failed to save settings');
    }
    setSavingSettings(false);
  };

  useEffect(() => {
    checkConnection();
    loadNotificationSettings();
    loadDepartmentMessages();
  }, [checkConnection, loadNotificationSettings, loadDepartmentMessages]);

  useEffect(() => {
    if (activeTab === 'inbox') {
      loadEmails();
    } else if (activeTab === 'sent') {
      loadSentEmails();
    } else if (activeTab === 'departments') {
      loadDepartmentMessages();
    }
  }, [activeTab, loadEmails, loadSentEmails, loadDepartmentMessages]);

  // Open email and mark as read
  const openEmail = (email) => {
    setSelectedEmail(email);
    if (email.id) {
      markAsRead(email.id);
    }
  };

  // Send email
  const handleSendEmail = async () => {
    // Check if sending to department or external email
    if (composeData.isToDepartment) {
      // Inter-department message
      if (!composeData.toDepartment || !composeData.subject || !composeData.body) {
        alert('Please select a department and fill in all fields');
        return;
      }
      
      setSending(true);
      try {
        await axios.post(`${API}/department-messages?sender_id=${adminUser?.id || ''}&sender_name=${adminUser?.full_name || ''}`, {
          from_department: department,
          to_department: composeData.toDepartment,
          subject: composeData.subject,
          message: composeData.body
        });
        
        alert(`Message sent to ${DEPARTMENT_EMAILS[composeData.toDepartment]?.name || composeData.toDepartment}! An email notification was also sent.`);
        setComposeData({ to: '', subject: '', body: '', toDepartment: '', isToDepartment: false });
        setActiveTab('departments');
        loadDepartmentMessages();
      } catch (error) {
        console.error('Error sending department message:', error);
        alert(error.response?.data?.detail || 'Failed to send message');
      }
      setSending(false);
      return;
    }
    
    // Regular email
    if (!composeData.to || !composeData.subject || !composeData.body) {
      alert('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/email/send`, {
        from_department: department,
        to_emails: composeData.to.split(',').map(e => e.trim()),
        subject: composeData.subject,
        body_html: `<div style="font-family: Arial, sans-serif; line-height: 1.6;">${composeData.body.replace(/\n/g, '<br>')}</div>`,
        sender_name: adminUser?.full_name || deptConfig.name
      });
      
      // Store sent email locally
      const sentEmail = {
        id: `sent_${Date.now()}`,
        to: composeData.to,
        subject: composeData.subject,
        body: composeData.body,
        date: new Date().toISOString(),
        from: deptConfig.email
      };
      const newSentEmails = [sentEmail, ...sentEmails];
      setSentEmails(newSentEmails);
      localStorage.setItem(`sent_emails_${department}`, JSON.stringify(newSentEmails.slice(0, 100)));
      
      alert('Email sent successfully!');
      setComposeData({ to: '', subject: '', body: '', toDepartment: '', isToDepartment: false });
      setActiveTab('sent');
    } catch (error) {
      console.error('Error sending email:', error);
      alert(error.response?.data?.detail || 'Failed to send email');
    }
    setSending(false);
  };

  // Get unread count for emails
  const unreadCount = emails.filter(e => !readEmails.has(e.id)).length;
  
  // Get unread count for department messages
  const deptUnreadCount = deptMessages.filter(m => !m.is_read && m.to_department === department).length;

  const colorClasses = {
    emerald: 'from-emerald-500 to-green-500',
    purple: 'from-purple-500 to-violet-500',
    orange: 'from-orange-500 to-amber-500',
    slate: 'from-slate-600 to-slate-700',
    red: 'from-red-500 to-rose-500',
    green: 'from-green-500 to-emerald-500',
    indigo: 'from-indigo-500 to-purple-500',
    blue: 'from-blue-500 to-indigo-500'
  };

  const gradientClass = colorClasses[deptConfig.color] || colorClasses.blue;

  // Email list item component
  const EmailListItem = ({ email, isSent = false }) => {
    const isRead = readEmails.has(email.id);
    const isStarred = starredEmails.has(email.id);
    const emailId = email.id || email.message_id || `email_${email.date}`;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={() => openEmail({ ...email, id: emailId })}
        className={`p-3 sm:p-4 rounded-xl cursor-pointer transition-all border-2 ${
          isRead 
            ? 'bg-white border-slate-100 hover:border-slate-200' 
            : 'bg-blue-50 border-blue-100 hover:border-blue-200'
        }`}
      >
        <div className="flex items-start gap-3">
          {/* Read/Unread indicator */}
          <div className="flex flex-col items-center gap-2 pt-1">
            {!isSent && (
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isRead ? 'bg-slate-300' : 'bg-blue-500'}`} />
            )}
            <button 
              onClick={(e) => toggleStar(emailId, e)}
              className="p-1 hover:bg-slate-100 rounded transition-colors"
            >
              {isStarred ? (
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              ) : (
                <StarOff className="w-4 h-4 text-slate-300 hover:text-amber-400" />
              )}
            </button>
          </div>
          
          {/* Email content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className={`truncate ${isRead ? 'font-medium text-slate-700' : 'font-bold text-slate-900'}`}>
                {isSent ? `To: ${email.to}` : (email.from_name || email.from || 'Unknown sender')}
              </p>
              <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">
                {email.date ? new Date(email.date).toLocaleDateString('en-GB', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric' 
                }) : ''}
              </span>
            </div>
            <p className={`text-sm truncate ${isRead ? 'text-slate-600' : 'text-slate-800 font-medium'}`}>
              {email.subject || '(No subject)'}
            </p>
            <p className="text-xs text-slate-400 truncate mt-1">
              {email.snippet || email.body?.substring(0, 100) || ''}
            </p>
          </div>
          
          {/* Indicators */}
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {email.has_attachments && (
              <Paperclip className="w-4 h-4 text-slate-400" />
            )}
            {isSent && <SendHorizontal className="w-4 h-4 text-green-500" />}
          </div>
        </div>
      </motion.div>
    );
  };

  // Full email view modal — extracted to ./email/EmailViewModal.js

  if (compact) {
    // Compact version for embedding in department sections
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${gradientClass} p-4 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5" />
              <div>
                <h3 className="font-bold text-sm">{deptConfig.name} Email</h3>
                <p className="text-xs opacity-80">{deptConfig.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {connectionStatus?.connected ? (
                <span className="flex items-center gap-1 text-xs bg-white/20 px-2 py-1 rounded-full">
                  <CheckCircle2 className="w-3 h-3" /> Connected
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs bg-red-500/30 px-2 py-1 rounded-full">
                  <AlertCircle className="w-3 h-3" /> Offline
                </span>
              )}
              <button
                onClick={() => { checkConnection(); loadEmails(); loadSentEmails(); }}
                className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100">
          <button
            onClick={() => setActiveTab('compose')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'compose' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Send className="w-4 h-4" />
            Compose
          </button>
          <button
            onClick={() => setActiveTab('inbox')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors relative ${
              activeTab === 'inbox' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Inbox className="w-4 h-4" />
            Inbox
            {unreadCount > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white rounded-full text-xs font-bold">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'sent' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <SendHorizontal className="w-4 h-4" />
            Sent
            {sentEmails.length > 0 && (
              <span className="px-1.5 py-0.5 bg-green-100 text-green-600 rounded-full text-xs">
                {sentEmails.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('departments')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'departments' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Depts
            {deptUnreadCount > 0 && (
              <span className="px-1.5 py-0.5 bg-purple-500 text-white rounded-full text-xs font-bold">
                {deptUnreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'settings' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <AnimatePresence mode="wait">
            {activeTab === 'compose' && (
              <motion.div
                key="compose"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-3"
              >
                {/* Toggle: Email vs Department */}
                <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                  <button
                    onClick={() => setComposeData(prev => ({ ...prev, isToDepartment: false }))}
                    className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${
                      !composeData.isToDepartment 
                        ? 'bg-white shadow text-blue-600' 
                        : 'text-slate-500'
                    }`}
                  >
                    External Email
                  </button>
                  <button
                    onClick={() => setComposeData(prev => ({ ...prev, isToDepartment: true }))}
                    className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${
                      composeData.isToDepartment 
                        ? 'bg-white shadow text-purple-600' 
                        : 'text-slate-500'
                    }`}
                  >
                    To Department
                  </button>
                </div>
                
                {composeData.isToDepartment ? (
                  /* Department Selector */
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Select Department</label>
                    <select
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={composeData.toDepartment}
                      onChange={(e) => setComposeData(prev => ({ ...prev, toDepartment: e.target.value }))}
                    >
                      <option value="">Choose a department...</option>
                      <option value="all">📢 All Departments (Broadcast)</option>
                      {Object.entries(DEPARTMENT_EMAILS)
                        .filter(([key]) => key !== department && key !== 'communications' && key !== 'secretarial')
                        .map(([key, config]) => (
                          <option key={key} value={key}>{config.name}</option>
                        ))
                      }
                    </select>
                  </div>
                ) : (
                  /* Email To Field */
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">To</label>
                    <input
                      type="text"
                      placeholder="recipient@example.com"
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={composeData.to}
                      onChange={(e) => setComposeData(prev => ({ ...prev, to: e.target.value }))}
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Subject</label>
                  <input
                    type="text"
                    placeholder="Email subject"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={composeData.subject}
                    onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Message</label>
                  <textarea
                    rows={4}
                    placeholder="Write your message..."
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    value={composeData.body}
                    onChange={(e) => setComposeData(prev => ({ ...prev, body: e.target.value }))}
                  />
                </div>
                <button
                  onClick={handleSendEmail}
                  disabled={sending || (!composeData.isToDepartment && !connectionStatus?.connected)}
                  className={`w-full py-2.5 rounded-lg text-white font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                    sending || !connectionStatus?.connected
                      ? 'bg-slate-400 cursor-not-allowed'
                      : `bg-gradient-to-r ${gradientClass} hover:shadow-lg`
                  }`}
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Email
                    </>
                  )}
                </button>
              </motion.div>
            )}

            {activeTab === 'inbox' && (
              <motion.div
                key="inbox"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : emails.length === 0 ? (
                  <div className="text-center py-8">
                    <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">No emails in inbox</p>
                    <button
                      onClick={loadEmails}
                      className="mt-3 text-blue-600 text-sm hover:underline flex items-center gap-1 mx-auto"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Refresh
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {emails.map((email, idx) => (
                      <EmailListItem key={email.id || idx} email={email} />
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'sent' && (
              <motion.div
                key="sent"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : sentEmails.length === 0 ? (
                  <div className="text-center py-8">
                    <SendHorizontal className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">No sent emails</p>
                    <p className="text-slate-400 text-xs mt-1">Emails you send will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {sentEmails.map((email, idx) => (
                      <EmailListItem key={email.id || idx} email={email} isSent />
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'settings' && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="space-y-4"
              >
                <div className="text-center mb-4">
                  <Bell className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <h4 className="font-semibold text-slate-800">Notification Settings</h4>
                </div>

                {/* Notification Type */}
                <div className="grid grid-cols-2 gap-2">
                  {NOTIFICATION_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => setNotificationSettings(prev => ({ ...prev, notification_type: type.value }))}
                        className={`p-2 rounded-lg text-left transition-all ${
                          notificationSettings.notification_type === type.value
                            ? 'bg-blue-100 border-2 border-blue-500 text-blue-700'
                            : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          <span className="text-xs font-medium">{type.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Save Button */}
                <button
                  onClick={saveNotificationSettings}
                  disabled={savingSettings}
                  className={`w-full py-2.5 rounded-lg text-white font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                    savingSettings
                      ? 'bg-slate-400 cursor-not-allowed'
                      : `bg-gradient-to-r ${gradientClass} hover:shadow-lg`
                  }`}
                >
                  {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </button>
              </motion.div>
            )}

            {activeTab === 'departments' && (
              <motion.div
                key="departments"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                {deptMessages.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">No inter-department messages</p>
                    <p className="text-slate-400 text-xs mt-1">Use Compose → To Department to message other departments</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {deptMessages.map((msg, idx) => {
                      const isIncoming = msg.to_department === department;
                      const otherDept = isIncoming ? msg.from_department : msg.to_department;
                      const otherDeptConfig = DEPARTMENT_EMAILS[otherDept] || { name: otherDept, color: 'slate' };
                      
                      return (
                        <div
                          key={msg.id || idx}
                          className={`p-3 rounded-xl transition-all cursor-pointer border-2 ${
                            isIncoming && !msg.is_read
                              ? 'bg-purple-50 border-purple-200 hover:border-purple-300'
                              : 'bg-white border-slate-100 hover:border-slate-200'
                          }`}
                          onClick={async () => {
                            if (isIncoming && !msg.is_read) {
                              await axios.patch(`${API}/department-messages/${msg.id}/read`);
                              loadDepartmentMessages();
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${
                              isIncoming && !msg.is_read ? 'bg-purple-500' : 'bg-slate-300'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  isIncoming ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                }`}>
                                  {isIncoming ? `From: ${otherDeptConfig.name}` : `To: ${otherDeptConfig.name}`}
                                </span>
                                {msg.is_broadcast && (
                                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
                                    Broadcast
                                  </span>
                                )}
                              </div>
                              <p className={`text-sm font-medium ${isIncoming && !msg.is_read ? 'text-purple-800' : 'text-slate-700'}`}>
                                {msg.subject}
                              </p>
                              <p className="text-xs text-slate-500 truncate mt-1">{msg.message}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(msg.created_at).toLocaleDateString()} • {msg.sender_name}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Email Detail Modal */}
        <AnimatePresence>
          <EmailViewModal
            email={selectedEmail}
            gradientClass={gradientClass}
            isRead={selectedEmail ? readEmails.has(selectedEmail.id) : false}
            isStarred={selectedEmail ? starredEmails.has(selectedEmail.id) : false}
            onClose={() => setSelectedEmail(null)}
            onToggleStar={toggleStar}
          />
        </AnimatePresence>
      </div>
    );
  }

  // Full version (standalone page) - same as compact but larger
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`bg-gradient-to-r ${gradientClass} rounded-3xl p-6 text-white shadow-xl`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white/20">
              <Mail className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{deptConfig.name} Email</h2>
              <p className="text-white/80">{deptConfig.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {connectionStatus?.connected ? (
              <span className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-xl">
                <CheckCircle2 className="w-5 h-5" /> Connected
              </span>
            ) : (
              <span className="flex items-center gap-2 bg-red-500/30 px-4 py-2 rounded-xl">
                <AlertCircle className="w-5 h-5" /> Disconnected
              </span>
            )}
            <button
              onClick={() => { checkConnection(); loadEmails(); loadSentEmails(); }}
              className="p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="flex border-b border-slate-100">
          <button
            onClick={() => setActiveTab('compose')}
            className={`flex-1 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'compose' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Send className="w-5 h-5" />
            Compose Email
          </button>
          <button
            onClick={() => setActiveTab('inbox')}
            className={`flex-1 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'inbox' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Inbox className="w-5 h-5" />
            Inbox
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-red-500 text-white rounded-full text-sm font-bold">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            className={`flex-1 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'sent' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <SendHorizontal className="w-5 h-5" />
            Sent
            {sentEmails.length > 0 && (
              <span className="px-2 py-0.5 bg-green-100 text-green-600 rounded-full text-sm">
                {sentEmails.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 py-4 font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'settings' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Settings className="w-5 h-5" />
            Settings
          </button>
        </div>

        <div className="p-6">
          <AnimatePresence mode="wait">
            {activeTab === 'compose' && (
              <motion.div
                key="compose-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="max-w-2xl mx-auto space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">To</label>
                  <input
                    type="text"
                    placeholder="recipient@example.com (separate multiple with comma)"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={composeData.to}
                    onChange={(e) => setComposeData(prev => ({ ...prev, to: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                  <input
                    type="text"
                    placeholder="Email subject"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={composeData.subject}
                    onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                  <textarea
                    rows={10}
                    placeholder="Write your message..."
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    value={composeData.body}
                    onChange={(e) => setComposeData(prev => ({ ...prev, body: e.target.value }))}
                  />
                </div>
                <button
                  onClick={handleSendEmail}
                  disabled={sending || !connectionStatus?.connected}
                  className={`w-full py-3 rounded-xl text-white font-medium flex items-center justify-center gap-2 transition-all ${
                    sending || !connectionStatus?.connected
                      ? 'bg-slate-400 cursor-not-allowed'
                      : `bg-gradient-to-r ${gradientClass} hover:shadow-lg hover:scale-[1.01]`
                  }`}
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Email
                    </>
                  )}
                </button>
              </motion.div>
            )}

            {activeTab === 'inbox' && (
              <motion.div
                key="inbox-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : emails.length === 0 ? (
                  <div className="text-center py-12">
                    <Inbox className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 text-lg">No emails in inbox</p>
                    <button
                      onClick={loadEmails}
                      className="mt-4 text-blue-600 hover:underline flex items-center gap-2 mx-auto"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Refresh Inbox
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Email summary */}
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
                      <div className="flex items-center gap-4">
                        <span className="text-slate-600">
                          <strong>{emails.length}</strong> emails
                        </span>
                        {unreadCount > 0 && (
                          <span className="text-blue-600 font-medium">
                            {unreadCount} unread
                          </span>
                        )}
                      </div>
                      <button
                        onClick={loadEmails}
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Refresh
                      </button>
                    </div>
                    {emails.map((email, idx) => (
                      <EmailListItem key={email.id || idx} email={email} />
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'sent' && (
              <motion.div
                key="sent-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : sentEmails.length === 0 ? (
                  <div className="text-center py-12">
                    <SendHorizontal className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 text-lg">No sent emails</p>
                    <p className="text-slate-400 mt-2">Emails you send will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
                      <span className="text-slate-600">
                        <strong>{sentEmails.length}</strong> sent emails
                      </span>
                    </div>
                    {sentEmails.map((email, idx) => (
                      <EmailListItem key={email.id || idx} email={email} isSent />
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'settings' && (
              <motion.div
                key="settings-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="max-w-xl mx-auto"
              >
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <Bell className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Notification Settings</h3>
                  <p className="text-slate-500 mt-1">Customize your email notifications</p>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-3">
                    {NOTIFICATION_TYPES.map(type => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.value}
                          onClick={() => setNotificationSettings(prev => ({ ...prev, notification_type: type.value }))}
                          className={`p-4 rounded-xl text-left transition-all ${
                            notificationSettings.notification_type === type.value
                              ? 'bg-blue-50 border-2 border-blue-500 shadow-md'
                              : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-1">
                            <Icon className={`w-5 h-5 ${notificationSettings.notification_type === type.value ? 'text-blue-600' : 'text-slate-400'}`} />
                            <span className="font-semibold">{type.label}</span>
                          </div>
                          <p className="text-xs text-slate-500 ml-8">{type.description}</p>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={saveNotificationSettings}
                    disabled={savingSettings}
                    className={`w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all ${
                      savingSettings
                        ? 'bg-slate-400 cursor-not-allowed'
                        : `bg-gradient-to-r ${gradientClass} hover:shadow-lg`
                    }`}
                  >
                    {savingSettings ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    Save Settings
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Email Detail Modal */}
      <AnimatePresence>
        <EmailViewModal
          email={selectedEmail}
          gradientClass={gradientClass}
          isRead={selectedEmail ? readEmails.has(selectedEmail.id) : false}
          isStarred={selectedEmail ? starredEmails.has(selectedEmail.id) : false}
          onClose={() => setSelectedEmail(null)}
          onToggleStar={toggleStar}
        />
      </AnimatePresence>
    </div>
  );
};

export default DepartmentEmailModule;
